import { IconFont, Tooltip } from '@byecode/ui'
import React from 'react'

import { useLanguageContext } from '../../../contexts'
import type { I18TranslationKey } from '../../../types'
import { FillPickerCore } from '../../FillPicker'
import { BasicActionIcon, CustomActionIcon, DropdownActionButton } from '../Toolbar/styles'
import type { TableActionType } from './core'
import {
    ALIGN_OPTIONS,
    CONTEXT_MENU_ACTION_CONFIG,
    STRUCTURE_ACTION_CONFIG,
    type TableStructureActionType
} from './TableControls.config'
import type { SelectionCapabilities } from './TableControls.helpers'
import { preventToolbarBlur } from './TableControls.helpers'
import {
    CombineButtonContainer,
    ContextMenuDivider,
    ContextMenuItem,
    ToolbarDropdownContainer,
    ToolbarDropdownItem,
    ToolbarDropdownSurface
} from './TableControls.styles'

export const TableToolButton: React.FC<{ title: string; icon: string; active?: boolean; onClick: () => void }> = ({
    title,
    icon,
    active,
    onClick
}) => {
    return (
        <Tooltip title={title}>
            <BasicActionIcon
                className={active ? 'active' : undefined}
                onMouseDown={event => {
                    event.preventDefault()
                    event.stopPropagation()
                }}
                onClick={onClick}
            >
                <IconFont type={icon} />
            </BasicActionIcon>
        </Tooltip>
    )
}

export const ColorPickerButton: React.FC<{
    opened: boolean
    onOpen: () => void
    onToggle: () => void
    icon: string
    title: string
    defaultValue?: string
    onChange: (value?: string) => void
}> = ({ opened, onOpen, onToggle, icon, title, defaultValue, onChange }) => {
    return (
        <ToolbarDropdownContainer data-rich-text-table-control="">
            <Tooltip title={title}>
                <CombineButtonContainer data-rich-text-table-control="">
                    <CustomActionIcon onMouseDown={preventToolbarBlur} onPointerDown={preventToolbarBlur} onClick={onOpen}>
                        <IconFont type={icon} style={{ color: defaultValue }} />
                    </CustomActionIcon>
                    <DropdownActionButton onMouseDown={preventToolbarBlur} onPointerDown={preventToolbarBlur} onClick={onToggle}>
                        <IconFont size={10} type="ArrowDownSmallGray" color="var(--color-gray-500)" />
                    </DropdownActionButton>
                </CombineButtonContainer>
            </Tooltip>
            {opened && (
                <ToolbarDropdownSurface
                    data-rich-text-table-control=""
                    width={280}
                    style={{ padding: '8px 12px' }}
                    onMouseDown={preventToolbarBlur}
                    onPointerDown={preventToolbarBlur}
                >
                    <div data-rich-text-table-control="">
                        <FillPickerCore
                            hideLibrary
                            defaultValue={defaultValue}
                            onChange={value => {
                                onChange(value)
                            }}
                        />
                    </div>
                </ToolbarDropdownSurface>
            )}
        </ToolbarDropdownContainer>
    )
}

export const AlignPickerButton: React.FC<{
    opened: boolean
    onToggle: () => void
    onClose: () => void
    activeValue: 'left' | 'center' | 'right' | null
    onSelect: (value: 'left' | 'center' | 'right' | null) => void
}> = ({ opened, onToggle, onClose, activeValue, onSelect }) => {
    const { convertTextByLanguage } = useLanguageContext()
    const activeOption = ALIGN_OPTIONS.find(item => item.value === activeValue) ?? ALIGN_OPTIONS[0]

    return (
        <ToolbarDropdownContainer data-rich-text-table-control="">
            <Tooltip title={convertTextByLanguage(activeOption.label as I18TranslationKey)}>
                <DropdownActionButton
                    data-rich-text-table-control=""
                    className={activeValue ? 'active' : undefined}
                    onMouseDown={preventToolbarBlur}
                    onPointerDown={preventToolbarBlur}
                    onClick={onToggle}
                >
                    <IconFont size={16} type={activeOption.icon} />
                    <IconFont size={10} type="ArrowDownSmallGray" color="var(--color-gray-500)" />
                </DropdownActionButton>
            </Tooltip>
            {opened && (
                <ToolbarDropdownSurface
                    data-rich-text-table-control=""
                    width={140}
                    onMouseDown={preventToolbarBlur}
                    onPointerDown={preventToolbarBlur}
                >
                    {ALIGN_OPTIONS.map(item => (
                        <ToolbarDropdownItem
                            key={item.value}
                            active={item.value === activeValue}
                            onMouseDown={preventToolbarBlur}
                            onPointerDown={preventToolbarBlur}
                            onClick={() => {
                                onClose()
                                onSelect(item.value)
                            }}
                        >
                            <IconFont type={item.icon} />
                            {convertTextByLanguage(item.label as I18TranslationKey)}
                        </ToolbarDropdownItem>
                    ))}
                    <ToolbarDropdownItem
                        onMouseDown={preventToolbarBlur}
                        onPointerDown={preventToolbarBlur}
                        onClick={() => {
                            onClose()
                            onSelect(null)
                        }}
                    >
                        {convertTextByLanguage('clearFormat')}
                    </ToolbarDropdownItem>
                </ToolbarDropdownSurface>
            )}
        </ToolbarDropdownContainer>
    )
}

export const TableStructureActions: React.FC<{
    actions: TableStructureActionType[]
    onAction: (action: TableActionType) => boolean
}> = ({ actions, onAction }) => {
    const { convertTextByLanguage } = useLanguageContext()
    return (
        <>
            {actions.map(action => (
                <TableToolButton
                    key={action}
                    title={convertTextByLanguage(STRUCTURE_ACTION_CONFIG[action].label)}
                    icon={STRUCTURE_ACTION_CONFIG[action].icon}
                    onClick={() => onAction(action)}
                />
            ))}
        </>
    )
}

export const TableContextMenuContent: React.FC<{
    capabilities: SelectionCapabilities | null
    onAction: (action: TableActionType) => boolean
}> = ({ capabilities, onAction }) => {
    const { convertTextByLanguage } = useLanguageContext()
    const contextMenuActions = capabilities?.contextMenuActions ?? []
    const sections = Array.from(new Set(contextMenuActions.map(action => CONTEXT_MENU_ACTION_CONFIG[action].section)))

    return (
        <>
            {sections.map((section, sectionIndex) => (
                <React.Fragment key={section}>
                    {sectionIndex > 0 && <ContextMenuDivider />}
                    {contextMenuActions
                        .filter(action => CONTEXT_MENU_ACTION_CONFIG[action].section === section)
                        .map(action => (
                            <ContextMenuItem
                                key={action}
                                danger={CONTEXT_MENU_ACTION_CONFIG[action].danger}
                                onClick={() => onAction(action)}
                            >
                                {convertTextByLanguage(CONTEXT_MENU_ACTION_CONFIG[action].label)}
                            </ContextMenuItem>
                        ))}
                </React.Fragment>
            ))}
        </>
    )
}
